'use client';

import React, { createContext, useMemo, useContext } from 'react';
import { useQuery } from 'react-query';

import productsService from '../../../data/services/products.service';
import { Product } from '../../../types/api/products/Product';

interface Props {
    defaultApiTreatment?: Product;
    treatmentId: number;
    children: React.ReactNode;
}
interface TreatmentApiContextValue {
    treatmentId: Props['treatmentId'];
    isLoading: boolean;
    error: null | Error;
    refetch: () => void;
    treatment?: Product;
    status?: number;
}

const TreatmentApiContext = createContext({
    isLoading: true,
});

const TreatmentApiProvider = ({ defaultApiTreatment, treatmentId, children }: Props) => {
    const cacheKey = `treatment-${treatmentId}`;
    const productCall = () => productsService.getProduct(treatmentId);
    const queryOptions = {
        cacheTime: 0,
        refetchOnWindowFocus: false,
        retry: false,
        // eslint-disable-next-line no-unneeded-ternary
        enabled: defaultApiTreatment ? false : true,
        initialData: {
            status: 200,
            statusText: 'successful',
            config: {},
            data: {
                data: defaultApiTreatment,
            },
            headers: {},
        }
    };

    // Request to get the treatment data from the API by treatment ID.
    // TODO: fix types
    // @ts-ignore
    const query = useQuery(cacheKey, productCall, queryOptions);

    const value = useMemo(() => {
        const { data, isFetching, error, refetch } = query;

        return {
            treatmentId,
            isLoading: isFetching,
            error,
            refetch,
            treatment: data?.data?.data,
            status: data?.data?.status,
        };
    }, [query, treatmentId]);

    return <TreatmentApiContext.Provider value={value}>{children}</TreatmentApiContext.Provider>;
};

export const useTreatmentApiContext = () => {
    const config = useContext(TreatmentApiContext) as TreatmentApiContextValue;

    if (config === undefined) {
        throw new Error('useTreatmentApiContext must be used within a TreatmentApiProvider.');
    }

    return config;
};

export default TreatmentApiProvider;
